import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Anchor, Compass, Sailboat, Users, Mail, Shuffle, AlertTriangle, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DIV_META = {
  navigators: { icon: Compass, accent: "bg-emerald-600", soft: "bg-emerald-50 border-emerald-200" },
  mariners:   { icon: Anchor,  accent: "bg-cyan-600",    soft: "bg-cyan-50 border-cyan-200" },
  skippers:   { icon: Sailboat, accent: "bg-indigo-600", soft: "bg-indigo-50 border-indigo-200" },
};

function DivisionPanel({ divSlug, divLabel, roster, teamsDoc, onSend, busy, dnd }) {
  const Meta = DIV_META[divSlug] || {};
  const Icon = Meta.icon || Users;
  const hasTeams = Array.isArray(teamsDoc?.teams) && teamsDoc.teams.length > 0;
  const drawnAt = teamsDoc?.drawn_at_utc;
  const sentAt = teamsDoc?.emails_sent_at_utc;
  const sentCount = teamsDoc?.emails_sent_count;

  return (
    <section
      className={`rounded-2xl border bg-white overflow-hidden shadow-sm ${Meta.soft || ""}`}
      data-testid={`orientation-division-${divSlug}`}
    >
      <header className={`px-5 py-4 text-white flex items-center justify-between gap-3 ${Meta.accent || "bg-slate-700"}`}>
        <div className="flex items-center gap-3 min-w-0">
          <Icon className="w-6 h-6 shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold truncate">{divLabel}</h2>
            <div className="text-xs opacity-90">
              {roster.length} first-year staff
              {hasTeams && ` · ${teamsDoc.teams.length} team${teamsDoc.teams.length === 1 ? "" : "s"}`}
              {drawnAt && ` · drawn ${new Date(drawnAt).toLocaleString()}`}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end shrink-0">
          <button
            onClick={() => onSend(divSlug)}
            disabled={busy || !hasTeams}
            className="btn-touch inline-flex items-center gap-1.5 px-3 rounded-lg bg-white text-slate-800 hover:bg-slate-100 disabled:opacity-50 text-sm font-semibold"
            data-testid={`send-btn-${divSlug}`}
          >
            <Mail className="w-4 h-4" /> Send Emails
          </button>
        </div>
      </header>

      <div className="px-5 py-4">
        {sentAt && (
          <div className="mb-3 flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle2 className="w-4 h-4" />
            <span><strong>{sentCount}</strong> emails sent {new Date(sentAt).toLocaleString()}</span>
          </div>
        )}

        {roster.length === 0 && (
          <div className="text-sm text-slate-500 italic">
            No 0–1 year staff in {divLabel} yet. Run a CampMinder sync if you expect some.
          </div>
        )}

        {!hasTeams && roster.length > 0 && (
          <div className="text-sm text-slate-600">
            Click <strong>Draw All 8 Teams</strong> at the top to allocate {divLabel}'s share of the 8 total teams.
            <details className="mt-2">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 text-xs">Show roster ({roster.length})</summary>
              <ul className="mt-2 text-sm grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {roster.map((m) => (
                  <li key={m.crew_id} className="py-0.5 text-slate-700">
                    {m.name || "(no name)"} <span className="text-xs text-slate-400">{m.email ? "" : "· no email"}</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {hasTeams && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {teamsDoc.teams.map((team) => {
              const isDropTarget = dnd?.dragOverLetter === team.letter;
              const isSelectedTarget = dnd?.selectedMemberId && dnd?.selectedFromLetter && dnd.selectedFromLetter !== team.letter;
              return (
              <div
                key={team.letter}
                className={`rounded-xl border p-3 transition-colors ${
                  isDropTarget
                    ? "border-blue-500 bg-blue-50 ring-2 ring-blue-300"
                    : isSelectedTarget
                    ? "border-dashed border-blue-400 bg-blue-50/40 cursor-pointer"
                    : "border-slate-200 bg-slate-50"
                }`}
                data-testid={`team-${divSlug}-${team.letter}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  dnd?.onDragOverTeam?.(team.letter);
                }}
                onDragLeave={() => dnd?.onDragLeaveTeam?.(team.letter)}
                onDrop={(e) => {
                  e.preventDefault();
                  const crewId = e.dataTransfer.getData("text/plain");
                  if (crewId) dnd?.onDrop?.(crewId, team.letter);
                }}
                onClick={() => {
                  // Tap-to-drop fallback for mobile: if a member is selected
                  // and this team isn't their current one, drop them here.
                  if (dnd?.selectedMemberId && dnd?.selectedFromLetter !== team.letter) {
                    dnd.onDrop(dnd.selectedMemberId, team.letter);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold text-lg text-slate-900">Group {team.letter}</div>
                  <span className="text-xs px-2 py-0.5 bg-white border border-slate-200 rounded-full text-slate-600 tabular-nums">
                    {(team.members || []).length}
                  </span>
                </div>
                <ul className="text-sm space-y-0.5">
                  {(team.members || []).map((m) => {
                    const isSelected = dnd?.selectedMemberId === m.crew_id;
                    return (
                    <li
                      key={m.crew_id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", m.crew_id);
                        dnd?.onDragStartMember?.(m.crew_id, team.letter);
                      }}
                      onDragEnd={() => dnd?.onDragEnd?.()}
                      onClick={(e) => {
                        e.stopPropagation();  // prevent the parent team-click
                        dnd?.onTapMember?.(m.crew_id, team.letter);
                      }}
                      className={`text-slate-700 leading-tight px-1.5 py-0.5 rounded cursor-grab active:cursor-grabbing select-none ${
                        isSelected ? "bg-blue-200 ring-2 ring-blue-400" : "hover:bg-white"
                      }`}
                      data-testid={`team-member-${m.crew_id}`}
                    >
                      {m.name || "(no name)"}
                      {!m.email && <span className="ml-1 text-[10px] text-amber-700">no email</span>}
                    </li>
                    );
                  })}
                </ul>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}


export default function Orientation() {
  const navigate = useNavigate();
  const [rosterByDiv, setRosterByDiv] = useState({});
  const [teamsByDiv, setTeamsByDiv] = useState({});
  const [meta, setMeta] = useState({ date: "" });
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(null);  // { slug, label, count }
  // Drag-and-drop / tap-to-move state. Mouse drag uses dragOverLetter for
  // hover highlighting; mobile uses selectedMemberId + tap-target.
  const [dragOverLetter, setDragOverLetter] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [selectedFromLetter, setSelectedFromLetter] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [rosterR, teamsR] = await Promise.all([
        api.get("/orientation/roster"),
        api.get("/orientation/teams"),
      ]);
      const rosterMap = {};
      (rosterR.data?.divisions || []).forEach((d) => { rosterMap[d.slug] = { label: d.label, members: d.members || [] }; });
      setRosterByDiv(rosterMap);
      setUnassigned(rosterR.data?.unassigned || []);
      const teamsMap = {};
      (teamsR.data?.divisions || []).forEach((d) => { teamsMap[d.slug] = d; });
      setTeamsByDiv(teamsMap);
      setMeta({ date: rosterR.data?.date || teamsR.data?.date || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orientation roster");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onAssign = async (crewId, division) => {
    try {
      await api.post("/orientation/assign", { crew_id: crewId, division });
      toast.success(`Assigned to ${division}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    }
  };

  const onDraw = async () => {
    setBusy(true);
    try {
      const r = await api.post("/orientation/draw", {});
      const total = r.data?.total_first_years || 0;
      const alloc = r.data?.allocation || {};
      const allocStr = Object.entries(alloc).map(([k, v]) => `${k}:${v}`).join(" · ");
      const overCap = r.data?.over_soft_cap_count || 0;
      toast.success(`8 teams drawn from ${total} staff (${allocStr})${overCap ? ` · ${overCap} team${overCap === 1 ? "" : "s"} over ${r.data?.soft_cap || 10}` : ""}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draw failed");
    } finally {
      setBusy(false);
    }
  };

  const onSendClick = (slug) => {
    const t = teamsByDiv[slug];
    const count = (t?.teams || []).reduce((acc, team) => acc + (team.members || []).filter((m) => m.email).length, 0);
    const label = rosterByDiv[slug]?.label || slug;
    setConfirmSend({ slug, label, count });
  };

  // ─── Drag-and-drop handlers ────────────────────────────────────────────
  // Move a staff member to a different team. Uses optimistic UI update so
  // the drop feels instant; rolls back + toasts the error if the API fails.
  const moveMember = async (crewId, toLetter) => {
    // Find current location for optimistic update + early bailout.
    let currentLetter = null;
    let memberDict = null;
    const newTeamsByDiv = JSON.parse(JSON.stringify(teamsByDiv));
    for (const slug of Object.keys(newTeamsByDiv)) {
      const teams = newTeamsByDiv[slug]?.teams || [];
      for (const t of teams) {
        const idx = (t.members || []).findIndex((m) => m.crew_id === crewId);
        if (idx >= 0) {
          memberDict = t.members[idx];
          currentLetter = t.letter;
          t.members.splice(idx, 1);
          break;
        }
      }
      if (memberDict) break;
    }
    if (currentLetter === toLetter) {
      // No-op: dropped on the same team. Clear selection silently.
      setSelectedMemberId(null);
      setSelectedFromLetter(null);
      return;
    }
    if (memberDict) {
      // Append to target team in any division.
      outer: for (const slug of Object.keys(newTeamsByDiv)) {
        const teams = newTeamsByDiv[slug]?.teams || [];
        for (const t of teams) {
          if (t.letter === toLetter) {
            t.members = [...(t.members || []), memberDict];
            break outer;
          }
        }
      }
      setTeamsByDiv(newTeamsByDiv);
    }
    setSelectedMemberId(null);
    setSelectedFromLetter(null);
    try {
      await api.post("/orientation/move-member", { crew_id: crewId, to_letter: toLetter });
      toast.success(`${memberDict?.name || "Staff"} → Group ${toLetter}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      // Roll back via a fresh reload so we stay in sync with the server.
      await reload();
    }
  };

  const dnd = {
    dragOverLetter,
    selectedMemberId,
    selectedFromLetter,
    onDragStartMember: (crewId, fromLetter) => {
      setSelectedMemberId(crewId);
      setSelectedFromLetter(fromLetter);
    },
    onDragEnd: () => {
      setDragOverLetter(null);
    },
    onDragOverTeam: (letter) => {
      if (dragOverLetter !== letter) setDragOverLetter(letter);
    },
    onDragLeaveTeam: (letter) => {
      if (dragOverLetter === letter) setDragOverLetter(null);
    },
    onDrop: (crewId, letter) => {
      setDragOverLetter(null);
      moveMember(crewId, letter);
    },
    // Mobile / tap-to-move: first tap selects, second tap (on a different
    // team) moves. Tapping the same member twice deselects.
    onTapMember: (crewId, fromLetter) => {
      if (selectedMemberId === crewId) {
        setSelectedMemberId(null);
        setSelectedFromLetter(null);
      } else {
        setSelectedMemberId(crewId);
        setSelectedFromLetter(fromLetter);
      }
    },
  };

  const onSendConfirm = async () => {
    if (!confirmSend) return;
    const { slug, label } = confirmSend;
    setBusy(true);
    setConfirmSend(null);
    try {
      const r = await api.post("/orientation/send-emails", { division: slug });
      const failed = r.data?.failed || 0;
      const sent = r.data?.sent || 0;
      const skipped = r.data?.skipped_no_email_count || 0;
      if (failed > 0) {
        toast.warning(`${label}: ${sent} sent, ${failed} failed${skipped ? `, ${skipped} skipped (no email)` : ""}`);
      } else {
        toast.success(`${label}: ${sent} emails sent${skipped ? `, ${skipped} skipped (no email)` : ""}`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const onSendCancel = () => setConfirmSend(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-touch inline-flex items-center gap-1 px-2 sm:px-3 rounded-lg text-slate-700 hover:bg-slate-100 shrink-0"
              data-testid="orientation-back"
            >
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-contain bg-white shadow-sm p-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold truncate">Rolling River — Admin</div>
              <h1 className="font-display text-base sm:text-lg font-bold text-slate-900 truncate">
                Orientation Games {meta.date && <span className="text-slate-500 font-normal text-sm">· {meta.date}</span>}
              </h1>
            </div>
          </div>
          <span className="px-2 sm:px-2.5 py-1 rounded-md bg-violet-600 text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] shrink-0">Admin</span>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-base sm:text-lg font-bold text-slate-900">Draw the day's teams</h2>
            <p className="text-sm text-slate-600">
              Randomly splits all <strong>0–1 year</strong> staff in Navigators / Mariners / Skippers into
              exactly <strong>8 teams</strong> total (Groups A–H). Each team stays in one division;
              counts per division are allocated proportionally to staff size.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              <span className="hidden sm:inline">Tip:</span>
              <span className="sm:hidden">Tip:</span>{" "}
              After drawing, <strong>drag</strong> staff between teams on desktop,
              or <strong>tap</strong> a name then tap a target team on mobile.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={onDraw}
              disabled={busy || loading}
              className="btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold"
              data-testid="draw-all-btn"
            >
              <Shuffle className="w-4 h-4" /> {busy ? "Drawing…" : "Draw All 8 Teams"}
            </button>
            <a
              href="/orientation/games?type=non_group"
              className="btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="games-nongroup-link"
              title="15-group sorter for non-Mariners/Skippers/Navigators staff"
            >
              <Shuffle className="w-4 h-4" /> Non-Group Game
            </a>
            <a
              href="/orientation/games?type=group"
              className="btn-touch inline-flex items-center gap-2 px-5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold"
              data-testid="games-group-link"
              title="15-group sorter for Mariners/Skippers/Navigators counselors, seeded by bunk cluster"
            >
              <Shuffle className="w-4 h-4" /> Group Game
            </a>
          </div>
        </div>

        {loading && (
          <div className="text-center text-slate-500 py-12">Loading roster…</div>
        )}

        {!loading && unassigned.length > 0 && (
          <section
            className="rounded-2xl border border-amber-300 bg-amber-50 overflow-hidden"
            data-testid="orientation-unassigned-panel"
          >
            <header className="px-5 py-3 bg-amber-200/60 text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <h2 className="font-display text-base font-bold">
                {unassigned.length} first-year staff need a division
              </h2>
            </header>
            <div className="px-5 py-3 text-sm text-amber-900">
              These first-years have no division set in CampMinder yet. Assign one below
              to include them in the draw, or update CampMinder + re-sync.
              <div className="mt-3 divide-y divide-amber-200">
                {unassigned.map((m) => (
                  <div
                    key={m.crew_id}
                    className="py-2 flex items-center justify-between gap-3"
                    data-testid={`unassigned-${m.crew_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{m.name || "(no name)"}</div>
                      {!m.email && <div className="text-xs text-amber-700">no email on file</div>}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {["navigators", "mariners", "skippers"].map((slug) => (
                        <button
                          key={slug}
                          onClick={() => onAssign(m.crew_id, slug)}
                          className="px-2.5 py-1 rounded-md bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 text-xs font-semibold"
                          data-testid={`assign-${m.crew_id}-${slug}`}
                        >
                          {slug[0].toUpperCase() + slug.slice(1, -1)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {!loading && ["navigators", "mariners", "skippers"].map((slug) => (
          <DivisionPanel
            key={slug}
            divSlug={slug}
            divLabel={rosterByDiv[slug]?.label || slug}
            roster={rosterByDiv[slug]?.members || []}
            teamsDoc={teamsByDiv[slug]}
            onSend={onSendClick}
            busy={busy}
            dnd={dnd}
          />
        ))}
      </main>

      {confirmSend && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center px-4" data-testid="orientation-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0" />
              <div className="flex-1">
                <h3 className="font-display text-lg font-bold text-slate-900">Send {confirmSend.label} emails?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will email <strong>{confirmSend.count}</strong> staff their Sample Day group letter, sent from
                  <code className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded">orientation@rrdcstaffsnapshot.com</code>.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={onSendCancel}
                className="btn-touch px-4 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold"
                data-testid="orientation-cancel-send"
              >
                Cancel
              </button>
              <button
                onClick={onSendConfirm}
                className="btn-touch px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                data-testid="orientation-confirm-send"
              >
                Send {confirmSend.count} emails
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
